/**
 * TerrainManager
 * Creates and manages terrain game objects for map tiles
 */

import type { WorldMap, Tile } from '@/qud/types';
import type { BlueprintRegistry } from '@/qud/ecs/BlueprintRegistry';
import type { GameObject } from '@/qud/ecs/GameObject';
import { Render } from '@/qud/ecs/parts/Render';
import { getColor, hexToRgb } from '@/qud/sprites/qudColors';

/**
 * Manages terrain game objects for the world map.
 * Creates game objects from blueprints and populates tile render data.
 */
export class TerrainManager {
  private gameObjects = new Map<string, GameObject>();
  private registry: BlueprintRegistry;

  constructor(registry: BlueprintRegistry) {
    this.registry = registry;
  }

  /**
   * Initialize terrain game objects for all tiles in the map.
   * Creates a GameObject for each tile's first blueprint and
   * populates the tile's sprite/spriteColors from the Render part.
   */
  initializeTerrain(worldMap: WorldMap): void {
    const startTime = performance.now();
    let created = 0;
    let skipped = 0;

    for (let y = 0; y < worldMap.height; y++) {
      for (let x = 0; x < worldMap.width; x++) {
        const tile = worldMap.tiles[y]?.[x];
        if (!tile || tile.blueprints.length === 0) {
          skipped++;
          continue;
        }

        // Use first blueprint for rendering (terrain layer)
        const blueprintName = tile.blueprints[0];
        if (this.registry.has(blueprintName)) {
          this.createTerrainObject(tile, blueprintName, x, y);
          created++;
        } else {
          skipped++;
        }
      }
    }

    const elapsed = performance.now() - startTime;
    console.log(
      `TerrainManager: Created ${created} terrain objects in ${elapsed.toFixed(1)}ms (${skipped} skipped)`
    );
  }

  /**
   * Create a terrain game object for a tile and populate its render data.
   */
  private createTerrainObject(tile: Tile, blueprintName: string, x: number, y: number): void {
    try {
      // Create game object - parts use global random for randomization
      const obj = this.registry.createObject(blueprintName);

      // Store reference
      const key = `${x},${y}`;
      this.gameObjects.set(key, obj);

      // Extract render data from the Render part
      const render = obj.getPart<Render>('Render');
      if (render) {
        const normalizedTile = render.getNormalizedTile();
        if (normalizedTile) {
          tile.sprite = normalizedTile;
          tile.spriteColors = render.getSpriteColors();

          // Pre-compute RGB values for rendering (avoids per-frame allocation)
          const fgCode = tile.spriteColors?.foreground || 'Y';
          const detailCode = tile.spriteColors?.detail || tile.spriteColors?.foreground || 'y';
          const fgRgb = hexToRgb(getColor(fgCode));
          const detailRgb = hexToRgb(getColor(detailCode));
          tile.renderColors = {
            fg: [fgRgb.r / 255, fgRgb.g / 255, fgRgb.b / 255],
            detail: [detailRgb.r / 255, detailRgb.g / 255, detailRgb.b / 255],
          };
        }
      }
    } catch {
      // Blueprint not found or other error - tile will use ASCII fallback
      // This is expected for some terrain types that aren't in our filtered blueprint set
    }
  }

  /**
   * Get the game object for a tile at the given coordinates.
   */
  getObjectAt(x: number, y: number): GameObject | undefined {
    return this.gameObjects.get(`${x},${y}`);
  }

  /**
   * Get the total number of managed terrain objects.
   */
  get objectCount(): number {
    return this.gameObjects.size;
  }
}
